/**
 * http.ts — ÚNICO lugar da SPA com fetch cru (guarda estrutural do plano,
 * §6.4: ESLint proíbe fetch fora daqui).
 *
 * Contrato: toda resposta de /api/v1 é o envelope {ok, data|error, meta}.
 * - CSRF: cookie XSRF-TOKEN (setado pelo backend em /auth/*) vira header
 *   X-CSRFToken em todo método não-GET; em 400 CSRF_INVALIDO renova o token
 *   uma vez e repete a requisição.
 * - 401: lança ApiError('NAO_AUTENTICADO') — o roteador decide o redirect.
 */

export interface Paginacao {
  pagina: number
  por_pagina: number
  total: number
  total_paginas: number
}

export interface Meta {
  request_id: string
  paginacao?: Paginacao | null
}

interface EnvelopeOk<T> {
  ok: true
  data: T
  meta: Meta
}

interface EnvelopeErro {
  ok: false
  error: { codigo: string; mensagem: string; detalhes?: unknown }
  meta: Meta
}

export class ApiError extends Error {
  constructor(
    public codigo: string,
    mensagem: string,
    public status: number,
    public detalhes?: unknown,
    public requestId?: string,
  ) {
    super(mensagem)
    this.name = 'ApiError'
  }
}

function lerCookie(nome: string): string | null {
  const par = document.cookie
    .split('; ')
    .find((linha) => linha.startsWith(`${nome}=`))
  return par ? decodeURIComponent(par.slice(nome.length + 1)) : null
}

async function renovarCsrf(): Promise<string> {
  const resp = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
  const corpo = (await resp.json()) as EnvelopeOk<{ csrf_token: string }>
  return corpo.data.csrf_token
}

export interface OpcoesApi {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** FormData para uploads multipart (não define Content-Type) */
  form?: FormData
  signal?: AbortSignal
}

async function executar<T>(
  caminho: string,
  opcoes: OpcoesApi,
  csrfRenovado: boolean,
): Promise<EnvelopeOk<T>> {
  const method = opcoes.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }
  let body: BodyInit | undefined

  if (opcoes.form) {
    body = opcoes.form
  } else if (opcoes.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(opcoes.body)
  }
  if (method !== 'GET') {
    const token = lerCookie('XSRF-TOKEN') ?? (await renovarCsrf())
    headers['X-CSRFToken'] = token
  }

  const resp = await fetch(caminho, {
    method,
    headers,
    body,
    credentials: 'same-origin',
    signal: opcoes.signal,
  })

  let corpo: EnvelopeOk<T> | EnvelopeErro
  try {
    corpo = (await resp.json()) as EnvelopeOk<T> | EnvelopeErro
  } catch {
    throw new ApiError(
      'ERRO_INTERNO',
      `Resposta não-JSON de ${caminho} (HTTP ${resp.status})`,
      resp.status,
    )
  }

  if (corpo.ok) return corpo

  if (corpo.error.codigo === 'CSRF_INVALIDO' && !csrfRenovado) {
    await renovarCsrf() // reseta o cookie; repete UMA vez
    return executar<T>(caminho, opcoes, true)
  }
  throw new ApiError(
    corpo.error.codigo,
    corpo.error.mensagem,
    resp.status,
    corpo.error.detalhes,
    corpo.meta.request_id,
  )
}

export async function api<T>(
  caminho: string,
  opcoes: OpcoesApi = {},
): Promise<T> {
  return (await executar<T>(caminho, opcoes, false)).data
}

/** Variante que preserva o envelope — para listas paginadas (meta.paginacao). */
export function apiComMeta<T>(
  caminho: string,
  opcoes: OpcoesApi = {},
): Promise<{ data: T; meta: Meta }> {
  return executar<T>(caminho, opcoes, false)
}

/**
 * TRANSITÓRIO — chama um endpoint LEGADO (fora de /api/v1), que não usa o
 * envelope {ok, data, meta}. Existe para a SPA reaproveitar rotas pesadas
 * ainda não migradas (extração de planilha, busca de códigos) em vez de
 * duplicá-las. Morre junto com as rotas legadas na F12.
 *
 * Fica aqui dentro de propósito: o guarda estrutural do plano (§6.4) exige
 * que TODO fetch da SPA esteja neste arquivo.
 */
export async function apiLegado<T>(
  caminho: string,
  opcoes: OpcoesApi = {},
): Promise<T> {
  const method = opcoes.method ?? 'GET'
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // as rotas legadas detectam AJAX por este header
    'X-Requested-With': 'XMLHttpRequest',
  }
  let body: BodyInit | undefined
  if (opcoes.form) {
    body = opcoes.form
  } else if (opcoes.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(opcoes.body)
  }
  if (method !== 'GET') {
    headers['X-CSRFToken'] = lerCookie('XSRF-TOKEN') ?? (await renovarCsrf())
  }

  const resp = await fetch(caminho, {
    method,
    headers,
    body,
    credentials: 'same-origin',
    signal: opcoes.signal,
  })
  let corpo: unknown
  try {
    corpo = await resp.json()
  } catch {
    throw new ApiError('ERRO_INTERNO',
      `Resposta não-JSON de ${caminho} (HTTP ${resp.status})`, resp.status)
  }
  if (!resp.ok) {
    const erro = corpo as { error?: string; message?: string }
    throw new ApiError('ERRO_INTERNO',
      erro.error ?? erro.message ?? `Erro em ${caminho}`, resp.status, corpo)
  }
  return corpo as T
}
